import type { AppEventMap } from './events';

type Handler<TPayload> = (payload: TPayload) => void;

const handlers = new Map<keyof AppEventMap, Set<Handler<unknown>>>();

function getOrCreateHandlers(event: keyof AppEventMap): Set<Handler<unknown>> {
  if (!handlers.has(event)) {
    handlers.set(event, new Set());
  }
  return handlers.get(event)!;
}

/**
 * Subscribe to an event. Returns an unsubscribe function.
 *
 * Usage:
 *   const unsubscribe = subscribe('AUTH_STATE_CHANGED', ({ user_id }) => { ... });
 *   unsubscribe(); // stop listening
 */
export function subscribe<K extends keyof AppEventMap>(
  event: K,
  handler: Handler<AppEventMap[K]>,
): () => void {
  const handlersForEvent = getOrCreateHandlers(event);
  handlersForEvent.add(handler as Handler<unknown>);

  return () => handlersForEvent.delete(handler as Handler<unknown>);
}

/**
 * Publish an event, calling all registered handlers with the given payload.
 *
 * Usage:
 *   publishAppEvent('AUTH_STATE_CHANGED', { user_id: '123' });
 */
export function publishAppEvent<K extends keyof AppEventMap>(
  event: K,
  payload: AppEventMap[K],
): void {
  const handlersForEvent = handlers.get(event);

  if (!handlersForEvent) return;

  handlersForEvent.forEach((handler) => handler(payload));
}
